import { useState, useRef, useCallback } from "react";
import { Upload, Image as ImageIcon, Loader2, Crop, Check, X, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { uploadArtwork, computeRatio } from "./printSizing";

const ACCEPT = ".png,.jpg,.jpeg,.webp,.pdf,.svg";
const MAX_BYTES = 10 * 1024 * 1024;

// Adjustable crop box over the preview. Ratio = (box.w * fullW) / (box.h * fullH).
function CropTool({ thumbnailUrl, fullWidth, fullHeight, box, setBox, ratio }) {
  const ref = useRef(null);
  const drag = useRef(null);

  const onPointerDown = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    drag.current = { mode, rect, startX: e.clientX, startY: e.clientY, box: { ...box } };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const onPointerMove = useCallback((e) => {
    if (!drag.current) return;
    const { mode, rect, startX, startY, box: b } = drag.current;
    const dx = (e.clientX - startX) / rect.width;
    const dy = (e.clientY - startY) / rect.height;
    let nb = { ...b };
    if (mode === "move") {
      nb.x = Math.min(Math.max(0, b.x + dx), 1 - b.w);
      nb.y = Math.min(Math.max(0, b.y + dy), 1 - b.h);
    } else if (mode === "br") {
      nb.w = Math.min(Math.max(0.05, b.w + dx), 1 - b.x);
      nb.h = Math.min(Math.max(0.05, b.h + dy), 1 - b.y);
    } else if (mode === "tl") {
      const nx = Math.min(Math.max(0, b.x + dx), b.x + b.w - 0.05);
      const ny = Math.min(Math.max(0, b.y + dy), b.y + b.h - 0.05);
      nb.w = b.w + (b.x - nx);
      nb.h = b.h + (b.y - ny);
      nb.x = nx;
      nb.y = ny;
    }
    setBox(nb);
  }, [setBox]);

  const onPointerUp = useCallback(() => {
    drag.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  return (
    <div className="relative inline-block select-none" ref={ref} style={{ touchAction: "none" }}>
      <img src={thumbnailUrl} alt="artwork preview" className="max-h-56 rounded border bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px]" draggable={false} />
      {box && (
        <div
          className="absolute border-2 border-blue-500 bg-blue-500/10 cursor-move"
          style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%` }}
          onPointerDown={onPointerDown("move")}
          data-testid="artwork-crop-box"
        >
          <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-nwse-resize" onPointerDown={onPointerDown("tl")} data-testid="artwork-crop-tl" />
          <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-nwse-resize" onPointerDown={onPointerDown("br")} data-testid="artwork-crop-br" />
        </div>
      )}
    </div>
  );
}

/**
 * Artwork upload + auto ratio detection for a Print Group.
 * Sets the RATIO only (and a preview thumbnail) — never the physical width/height mm.
 */
export function ArtworkUpload({ artwork, index, jobId, onUpdateFields }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const thumb = artwork.artwork_thumbnail_url;
  const fullW = artwork.artwork_full_width;
  const fullH = artwork.artwork_full_height;
  const box = artwork.artwork_crop_box;

  const currentRatio =
    box && fullW && fullH
      ? computeRatio(box.w * fullW, box.h * fullH)
      : { decimal: artwork.detected_ratio, display: null };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large (max 10 MB).");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const res = await uploadArtwork(file, jobId, artwork.id, setProgress);
      onUpdateFields({
        artwork_file_id: res.file_id,
        artwork_storage_path: res.storage_path,
        artwork_thumbnail_url: res.thumbnail_data_url || null,
        artwork_full_width: res.full_width,
        artwork_full_height: res.full_height,
        artwork_crop_box: res.bbox || null,
        detected_ratio: res.detected_ratio,
        artwork_filename: file.name,
      });
      if (res.detected_ratio && !res.thumbnail_data_url) {
        toast.success(`Ratio detected from ${res.kind?.toUpperCase() || "file"}: ${res.ratio_display || res.detected_ratio}`);
      } else if (res.detected_ratio) {
        toast.success("Artwork uploaded — adjust the crop box, then apply the ratio.");
      } else {
        toast.message("Uploaded, but couldn't auto-detect a ratio for this file.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const setBox = (nb) => onUpdateFields({ artwork_crop_box: nb });

  const applyRatio = () => {
    if (!currentRatio.decimal) return;
    onUpdateFields({
      ratio_decimal: currentRatio.decimal,
      ratio_display: currentRatio.display,
      ratio_source: "detected",
      sizing_confirmed: artwork.sizing_confirmed ? false : artwork.sizing_confirmed,
    });
    toast.success(`Applied ratio ${currentRatio.display || currentRatio.decimal} from artwork.`);
  };

  const clearArtwork = () => {
    onUpdateFields({
      artwork_file_id: null, artwork_storage_path: null, artwork_thumbnail_url: null,
      artwork_full_width: null, artwork_full_height: null, artwork_crop_box: null,
      detected_ratio: null, artwork_filename: null,
    });
  };

  return (
    <div className="mt-3" data-testid={`artwork-upload-${index}`}>
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={handleFile} data-testid={`artwork-file-input-${index}`} />

      {!thumb && !artwork.detected_ratio && (
        <Button type="button" variant="outline" size="sm" className="h-8 text-xs w-full border-dashed" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`artwork-upload-btn-${index}`}>
          {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading {progress}%</> : <><Upload className="w-3.5 h-3.5 mr-1" /> Upload artwork to detect ratio</>}
        </Button>
      )}

      {(thumb || artwork.detected_ratio) && (
        <div className="border rounded-lg p-3 bg-slate-50/60">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5 text-blue-500" /> Artwork {artwork.artwork_filename ? `— ${artwork.artwork_filename}` : ""}
            </span>
            <div className="flex items-center gap-1">
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => inputRef.current?.click()} data-testid={`artwork-replace-${index}`}>Replace</Button>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" onClick={clearArtwork} data-testid={`artwork-clear-${index}`}><X className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          {thumb ? (
            <>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-2">
                <Crop className="w-3 h-3" /> Drag the box to the printable artwork area — the ratio updates live.
              </div>
              <CropTool thumbnailUrl={thumb} fullWidth={fullW} fullHeight={fullH} box={box} setBox={setBox} ratio={currentRatio} />
            </>
          ) : (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <FileText className="w-4 h-4" /> No preview for this file — ratio detected from the file.
            </div>
          )}

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Detected ratio:</span>
              {currentRatio.decimal ? (
                <>
                  <span className="font-mono font-medium" data-testid={`artwork-detected-decimal-${index}`}>{currentRatio.decimal}</span>
                  {currentRatio.display && <Badge variant="secondary" className="text-[10px] font-mono">{currentRatio.display}</Badge>}
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
            <Button type="button" size="sm" className="h-7 text-xs" disabled={!currentRatio.decimal} onClick={applyRatio} data-testid={`artwork-apply-ratio-${index}`}>
              <Check className="w-3.5 h-3.5 mr-1" /> Apply ratio
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArtworkUpload;
